import React from 'react';
import { Switch, Route, Redirect, useParams } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import {
  SITE_DETAILS_OVERVIEW_PATH,
  SITE_DETAILS_CONTACTS_PATH,
  SITE_DETAILS_PROJECT_STATUS_PATH,
  SITE_DETAILS_NOTIFICATION_CENTER_PATH,
  SITE_DETAILS_NOTIFICATION_CENTER_CONTACT_PATH,
  pathToRoute,
} from '../constants/routes';
import SiteDetailsOverview from './SiteDetailsOverview';
import SiteDetailsContacts from './SiteDetailsContacts';
import ProjectStatusOverview from './ProjectStatusOverview';
import NotificationCenter from './NotificationCenter';
import {
  useStatusTrackerFeatureFlag,
  useNotificationCenterFeatureFlag,
  useHideFlags,
  useDepWarnFlags,
} from '../hooks/useGetFeatureFlag';
import { useUserAuthInfo } from '../components/auth';
import useSearchParams from '../hooks/useSearchParams';
import InfoHubRedirectBanner from '../components/InfoHubRedirectBanner';
import DeprecationWarningBanner from '../components/DeprecationWarningBanner';
import getInfoHubUrl from '../utils/get-infohub-url';
import PropTypes from 'prop-types';

const SiteDetailsPage = ({ currentOwnerContactId, loading }) => {
  const isProjectStatusTrackerEnabled = useStatusTrackerFeatureFlag();
  const isNotificationCenterFlagEnabled = useNotificationCenterFeatureFlag();
  const { isAdmin } = useUserAuthInfo();
  const { prospectId } = useParams();
  const searchParams = useSearchParams();

  const hideNav = searchParams.hideNav === 'true';
  const isNotificationCenterEnabled = isAdmin && isNotificationCenterFlagEnabled;
  const { hideContactsContent, hideInstallTrackerContent, hideNotificationCenterContent } = useHideFlags();
  const { depWarnSiteDetails, depWarnContacts, depWarnInstallTracker, depWarnNotificationCenter } = useDepWarnFlags();
  const prospectHomeUrl = getInfoHubUrl(`prospect/${prospectId}`);

  return (
    <Switch>
      <Route path={SITE_DETAILS_OVERVIEW_PATH}>
        <>
          {depWarnSiteDetails && <DeprecationWarningBanner title="Site Details" infohubUrl={prospectHomeUrl} />}
          <SiteDetailsOverview />
        </>
      </Route>
      <Route path={SITE_DETAILS_CONTACTS_PATH}>
        {hideContactsContent ? (
          <InfoHubRedirectBanner title="Contacts" infohubUrl={prospectHomeUrl} />
        ) : (
          <>
            {depWarnContacts && <DeprecationWarningBanner title="Contacts" infohubUrl={prospectHomeUrl} />}
            <SiteDetailsContacts />
          </>
        )}
      </Route>
      {isProjectStatusTrackerEnabled && (
        <Route path={SITE_DETAILS_PROJECT_STATUS_PATH}>
          {hideInstallTrackerContent ? (
            <InfoHubRedirectBanner title="Project Status" infohubUrl={prospectHomeUrl} />
          ) : (
            <>
              {depWarnInstallTracker && (
                <DeprecationWarningBanner title="Project Status" infohubUrl={prospectHomeUrl} />
              )}
              <ProjectStatusOverview prospectId={prospectId} />
            </>
          )}
        </Route>
      )}
      {isNotificationCenterEnabled && (
        <Route path={SITE_DETAILS_NOTIFICATION_CENTER_CONTACT_PATH}>
          {hideNotificationCenterContent ? (
            <InfoHubRedirectBanner title="Notification Center" infohubUrl={prospectHomeUrl} />
          ) : (
            <>
              {depWarnNotificationCenter && (
                <DeprecationWarningBanner title="Notification Center" infohubUrl={prospectHomeUrl} />
              )}
              <NotificationCenter prospectId={prospectId} />
            </>
          )}
        </Route>
      )}
      {isNotificationCenterEnabled && (
        <Route path={SITE_DETAILS_NOTIFICATION_CENTER_PATH}>
          {hideNotificationCenterContent ? (
            <InfoHubRedirectBanner title="Notification Center" infohubUrl={prospectHomeUrl} />
          ) : (
            <>
              {currentOwnerContactId ? (
                <Redirect
                  to={{
                    pathname: pathToRoute(SITE_DETAILS_NOTIFICATION_CENTER_CONTACT_PATH, {
                      prospectId,
                      contactId: currentOwnerContactId,
                    }),
                    search: hideNav ? '?hideNav=true' : '',
                  }}
                />
              ) : null}
              {!currentOwnerContactId && loading ? (
                <CircularProgress css={{ margin: 'auto', display: 'block' }} />
              ) : null}
              {!currentOwnerContactId && !loading ? (
                <Typography align="center">No Billing information found</Typography>
              ) : null}
            </>
          )}
        </Route>
      )}
      <Redirect to={pathToRoute(SITE_DETAILS_OVERVIEW_PATH, { prospectId })} />
    </Switch>
  );
};

export default SiteDetailsPage;

SiteDetailsPage.propTypes = {
  currentOwnerContactId: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
};
